import { pickOrganizedTourDealSpread } from "./pickOrganizedTourDealSpread";
import { resolveOrganizedTourCardImage } from "./resolveOrganizedTourCardImage";
import {
  ORGANIZED_TOUR_CURATED_GENERIC_TRAVEL_URL,
  organizedTourLooksLikeDirectImageUrl,
  organizedTourImageUrlLooksLowRes,
  upgradeOrganizedTourRemoteImageUrl,
} from "./organizedTourHeroImageSsot";
import { resolveOrganizedTourHeroVisual } from "./organizedTourHeroResolver";

function logDev(tag, payload) {
  if (!import.meta.env?.DEV) return;
  console.log(tag, payload);
}

function collectIataCodes(deal, normalized) {
  const out = [];
  const seen = new Set();
  const push = (c) => {
    const t = String(c || "").trim();
    if (!t) return;
    const upper = t.toUpperCase();
    if (seen.has(upper)) return;
    seen.add(upper);
    out.push(t);
  };
  const root = pickOrganizedTourDealSpread(deal);
  const tour = root.tour || {};
  const val = tour?.tourCities?.val ?? tour?.tourCities;
  const rawList = val ? (Array.isArray(val) ? val : [val]) : [];
  rawList.forEach((c) => push(c));

  const cities = normalized?.routeCities;
  if (Array.isArray(cities)) {
    cities.forEach((row) => push(row?.code || row?.CITYCODE));
  }
  return out;
}

function semanticBlob(normalized, deal) {
  const root = pickOrganizedTourDealSpread(deal);
  const hotel = root.hotel || {};
  return [
    normalized?.routeLabel,
    normalized?.tourName,
    root?.tourName,
    hotel?.city,
    hotel?.country,
    hotel?.destination,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Unified image SSOT — priority:
 * A. Supplier tour / reservation / hotel picture URLs from LDS
 * B→D.. destination hero resolver (IATA → text/country patterns → curated travel web hero)
 *
 * Does not rely on tour-cover.jpg; Tier E uses remote curated travel only.
 */
export function resolveOrganizedTourDestinationImage(deal, normalized) {
  const root = pickOrganizedTourDealSpread(deal);
  const dealId =
    normalized?.dealId || root?.dealId || root?.ldsDealId || deal?.dealId || "";

  const ldsBase = resolveOrganizedTourCardImage(deal);

  if (
    ldsBase.resolvedImage &&
    organizedTourLooksLikeDirectImageUrl(ldsBase.resolvedImage)
  ) {
    const raw = String(ldsBase.resolvedImage).trim();
    if (organizedTourImageUrlLooksLowRes(raw)) {
      logDev("[ORG_TOUR_IMAGE_LOW_QUALITY_BLOCKED]", {
        dealId,
        skipped: true,
        source: ldsBase.imageSource,
        urlSample: raw.slice(0, 140),
      });
    } else {
      const upgraded = upgradeOrganizedTourRemoteImageUrl(raw);
      logDev("[ORG_TOUR_IMAGE_SOURCE]", {
        dealId,
        tier: "A.supplier_or_resource_or_hotel",
        source: ldsBase.imageSource,
        upgraded: upgraded !== raw,
      });
      return {
        resolvedImage: upgraded,
        imageSource: ldsBase.imageSource,
        fallbackUsed: false,
        heroTier: `A.${ldsBase.imageSource}`,
      };
    }
  }

  if (ldsBase.resolvedImage && !organizedTourLooksLikeDirectImageUrl(ldsBase.resolvedImage)) {
    logDev("[ORG_TOUR_IMAGE_FALLBACK]", {
      dealId,
      phase: "skip_non_image_resource",
      reason: "url_not_image_like_html_or_pdf",
      urlSample: String(ldsBase.resolvedImage).slice(0, 120),
    });
  }

  const norm =
    normalized ||
    (() => {
      const d = root;
      const tour = d.tour || {};
      const val = tour?.tourCities?.val ?? tour?.tourCities;
      const codes = val ? (Array.isArray(val) ? val : [val]) : [];
      return {
        tourName: String(tour?.tourName || d?.tourName || ""),
        routeLabel: "",
        routeCities: codes.map((code) => ({
          code: String(code || "").trim(),
          label: String(code || "").trim(),
        })),
      };
    })();

  const airportCodes = collectIataCodes(deal, norm);
  const blob = semanticBlob(norm, deal);
  const hero = resolveOrganizedTourHeroVisual({
    airportCodes,
    semanticBlob: blob,
  });

  const upgradedHero = upgradeOrganizedTourRemoteImageUrl(hero.url);
  logDev("[ORG_TOUR_IMAGE_RESOLVE]", {
    dealId,
    tier: hero.tier,
    slug: hero.slug,
    airportCodesPreview: airportCodes.slice(0, 8),
  });

  if (hero.isGenericTravelFallback) {
    logDev("[ORG_TOUR_IMAGE_FALLBACK]", {
      dealId,
      reason: "no_destination_match_generic_travel_curated",
    });
    logDev("[ORG_TOUR_IMAGE_DESTINATION_MATCH]", {
      dealId,
      matched: false,
      airportCodes,
    });
    return {
      resolvedImage: ORGANIZED_TOUR_CURATED_GENERIC_TRAVEL_URL,
      imageSource: "curated.travel.generic",
      fallbackUsed: true,
      heroTier: hero.tier,
    };
  }

  logDev("[ORG_TOUR_IMAGE_DESTINATION_MATCH]", {
    dealId,
    matched: true,
    tier: hero.tier,
    airportCodes,
  });

  return {
    resolvedImage: upgradedHero || hero.url,
    imageSource: hero.tier,
    fallbackUsed: false,
    heroTier: hero.tier,
  };
}

/** @deprecated prefer inline [ORG_TOUR_IMAGE_*] logs */
export function traceOrganizedTourDestinationImage(dealId, payload) {
  logDev(payload?.fallbackUsed ? "[ORG_TOUR_IMAGE_FALLBACK]" : "[ORG_TOUR_IMAGE_RESOLVE]", {
    dealId,
    legacyTrace: true,
    ...payload,
  });
}
